/**
 * Clerk 인증 토큰 유틸리티
 *
 * ClerkProvider가 있는 페이지(auth routes)에서는 세션 토큰을 반환하고,
 * 없는 페이지(public routes)에서는 undefined를 반환합니다.
 */

interface ClerkInstance {
  session?: {
    getToken: () => Promise<string | null>;
  };
}

/**
 * Clerk 세션 토큰을 안전하게 가져옵니다.
 * - auth routes: Clerk JWT 반환
 * - public routes: undefined 반환
 */
export async function getAuthToken(): Promise<string | undefined> {
  if (typeof window === 'undefined') return undefined;

  const clerk = (window as unknown as { Clerk?: ClerkInstance }).Clerk;
  if (!clerk?.session) {
    // Clerk가 없는 환경 (공개 페이지) - 정상적인 경우
    return undefined;
  }

  try {
    const token = await clerk.session.getToken();
    return token ?? undefined;
  } catch (e) {
    // getToken() 실패는 실제 인증 오류 - 로깅 필요
    console.error('[AuthToken] Failed to get Clerk token:', e);
    return undefined;
  }
}

type AuthTokenProvider = () => Promise<string | undefined>;
type Fetcher = (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;

interface RealtimeAuthTokenOptions {
  agentId: string;
  realtimeProvider?: string | null;
  pathname?: string;
  authTokenProvider?: AuthTokenProvider;
  fetcher?: Fetcher;
}

/**
 * avatar_setup 전용 인증 토큰을 가져옵니다.
 * 로그인 세션은 Clerk JWT를, 공개 단일 slug 페이지는 단기 Realtime JWT를 사용합니다.
 */
export async function getRealtimeAuthToken({
  agentId,
  realtimeProvider,
  pathname,
  authTokenProvider = getAuthToken,
  fetcher = fetch,
}: RealtimeAuthTokenOptions): Promise<string | undefined> {
  const authToken = await authTokenProvider();
  if (authToken) return authToken;

  if (!agentId || realtimeProvider !== 'openai') return undefined;

  const currentPathname = pathname
    ?? (typeof window !== 'undefined' ? window.location.pathname : undefined);
  const slugMatch = currentPathname?.match(/^\/([^/]+)\/?$/);
  if (!slugMatch) return undefined;

  try {
    const slug = decodeURIComponent(slugMatch[1]);
    const response = await fetcher(
      `/api/public/s/${encodeURIComponent(slug)}/realtime-token`,
      { method: 'POST', cache: 'no-store' },
    );
    if (!response.ok) return undefined;

    const body = await response.json() as { token?: unknown; agent_id?: unknown };
    if (body.agent_id !== agentId || typeof body.token !== 'string') {
      return undefined;
    }
    return body.token;
  } catch {
    return undefined;
  }
}
